import { useEffect, useState } from 'react';

import {
  getCourseraPlusUpsellEligibilityData,
  isEligibleForCourseraPlusUpgrade,
  isFreemiumPricingEnabled,
} from 'bundles/coursera-plus-common/utils/courseraPlusUpgradeUtils';
import courseraPlusExperiments from 'bundles/epic/clients/courseraPlus';
import { SPECIALIZATION, VERIFIED_CERTIFICATE } from 'bundles/payments-common/common/ProductType';

type UseIsEligibleForCourseraPlusUpsellProps = {
  s12nId: string | undefined;
  courseId: string | undefined;
  skip?: boolean;
};

export const useIsEligibleForCourseraPlusUpsell = ({
  s12nId,
  courseId,
  skip = false,
}: UseIsEligibleForCourseraPlusUpsellProps): boolean => {
  const [isEligible, setIsEligible] = useState(false);
  const productItemId = s12nId || courseId;
  const productType = s12nId ? SPECIALIZATION : VERIFIED_CERTIFICATE;

  useEffect(() => {
    if (skip || !productItemId || !productType) {
      setIsEligible(false);
      return;
    }

    const checkEligibility = async () => {
      try {
        if (isFreemiumPricingEnabled()) {
          // For Freemium pricing experiment, we use the new API for upsell eligibility checks
          // If not eligible, eligibilityData from the BE will be null/undefined
          const eligibilityData = await getCourseraPlusUpsellEligibilityData(productItemId, productType);
          setIsEligible(!!eligibilityData);
        } else {
          // For non-Freemium pricing, we use the legacy API that returns a boolean
          const eligible = await isEligibleForCourseraPlusUpgrade(productItemId, productType);
          setIsEligible(eligible);
        }
      } catch {
        setIsEligible(false);
      }
    };

    checkEligibility();
  }, [productItemId, productType, skip]);

  return isEligible;
};

export const generateImpressionForPrePurchaseUpsellExperiment = (): void => {
  // Link to epic: https://tools.coursera.org/epic/experiment/RCl4RbamEfC-lAr_4rnFJw
  courseraPlusExperiments.get('prePurchaseUpsellVariant');
};

export const previewPrePurchaseUpsellVariant = (): 'control' | '1' | '2' | '3' => {
  const value = courseraPlusExperiments.preview('prePurchaseUpsellVariant');

  /* 
    Force variants '1' and '3' to show 'control' while still logging impressions for the original variant.
    Ref: https://coursera.slack.com/archives/C04HFFQ1PLM/p1764781815496499?thread_ts=1763577163.973969&cid=C04HFFQ1PLM
  */
  if (value === '1' || value === '3') {
    return 'control';
  }
  return value;
};
